"use client";

// ЕДИНОЕ модальное окно портала: <Modal> (форма/диалог) и <Drawer> (выдвижная панель).
//
// ЗАЧЕМ. До 08.08 общего компонента не было — 50 файлов рисовали оверлей руками
// (`fixed inset-0` + своя подложка + свой z-index). Отсюда разнобой, который
// видно глазом: подложка от bg-black/20 до bg-black/85 (13 вариантов), блюр то
// `-sm`, то `-md`, то никакой, а закрытие по Esc, клик по фону и блокировка
// прокрутки фона у каждого экрана свои — где-то есть, где-то забыли.
//
// ⚠️ ПОДЛОЖКА ЗАТЕМНЯЕТ, НО НЕ РАЗМЫВАЕТ (решение владельца 12.08.2026). Из окна
// поверх рабочего экрана часто сверяются с тем, что под ним — со строкой таблицы,
// с соседней карточкой; размытие это ломало. Обратно `backdrop-blur` не возвращать:
// 12.08 его сняли разом со ВСЕХ модальных подложек портала (кит + свои оверлеи
// в sales, telegram, workflows), чтобы не разъезжалось. Размытие осталось только
// на стеклянных ПОВЕРХНОСТЯХ (липкая шапка таблиц, узлы конструктора ботов) —
// там оно несёт смысл и к подложкам отношения не имеет.
//
// Здесь всё это сделано ОДИН раз:
//   • портал в <body> — панель не обрезается overflow-контейнером таблицы;
//   • Esc закрывает ВЕРХНЮЮ модалку (стек, а не все разом);
//   • клик по подложке закрывает, клик внутри панели — нет;
//   • прокрутка фона блокируется по счётчику: вложенная модалка при закрытии
//     больше не разблокирует страницу за открытую родительскую;
//   • вложенные окна сами поднимаются на слой выше (см. Z.MODAL_STEP);
//   • фокус уходит в панель и возвращается на исходный элемент при закрытии.
//
// Позиционирование: по умолчанию по центру. `placement="pointer"` открывает
// панель у курсора — так работают формы HR, поведение сохранено.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { IconClose } from "@/components/ui/icons";
import { confirmDialog } from "@/components/ui/confirm";
import { Z } from "@/lib/z";

/* ─────────────────── общая механика: стек, блокировка прокрутки ─────────────────── */

// Стек открытых окон: Esc должен закрывать только верхнее.
const stack: string[] = [];
let seq = 0;

// Блокировка прокрутки фона — по счётчику. Раньше каждое окно писало
// document.body.style.overflow напрямую, и закрытие вложенной формы возвращало
// прокрутку странице, хотя родительская форма ещё открыта.
let lockCount = 0;
let savedOverflow = "";
function lockScroll() {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
}
function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.body.style.overflow = savedOverflow;
}

/** Глубина вложенности — вложенное окно рисуется слоем выше родительского. */
const DepthContext = createContext(0);

// Точка последнего клика — для placement="pointer" (форма открывается у курсора).
let lastPointer = { x: 0, y: 0 };
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (e) => {
      lastPointer = { x: e.clientX, y: e.clientY };
    },
    true,
  );
}

/**
 * Пропсы обёртки окна: закрытие кликом по подложке.
 *
 * ⚠️ ЗАКРЫВАЕМ, ТОЛЬКО ЕСЛИ ЖЕСТ НА ПОДЛОЖКЕ И НАЧАЛСЯ. Браузер шлёт `click`
 * не туда, где отпустили кнопку, а в ОБЩЕГО ПРЕДКА точек нажатия и отпускания.
 * Поэтому выделение текста мышью в поле формы, законченное чуть за краем панели
 * (а на длинном ТЗ это происходит постоянно), прилетало подложке — и окно
 * закрывалось прямо во время правки текста. То же с перетаскиванием ползунка
 * прокрутки и уголка resize у textarea. Проверка «нажатие тоже было по фону»
 * снимает это и заодно гасит клик, «проваливающийся» на подложку после закрытия
 * выпадающего списка (его портал живёт в <body>, панель его не перехватывает).
 */
function useBackdropClose(close: () => void, enabled: boolean) {
  const startedOnBackdrop = useRef(false);
  // Фон — это сама обёртка либо помеченная подложка; всё внутри панели — нет.
  const isBackdrop = (e: { target: EventTarget | null; currentTarget: EventTarget | null }) =>
    e.target === e.currentTarget ||
    (e.target instanceof HTMLElement && e.target.dataset.backdrop === "1");
  return {
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
      startedOnBackdrop.current = isBackdrop(e);
    },
    onClick: enabled
      ? (e: ReactMouseEvent<HTMLDivElement>) => {
          if (!startedOnBackdrop.current || !isBackdrop(e)) return;
          close();
        }
      : undefined,
  };
}

// ⚠️ xxl — для окон, внутри которых живёт ЦЕЛЫЙ ЭКРАН портала: база знаний в
// кабинете (02.09.2026). При 960 px её две колонки — список и читалка — делили
// ширину пополам, и текст регламента читался в узкую колонку.
const SIZE_PX = { sm: 420, md: 560, lg: 720, xl: 960, xxl: 1320 } as const;
export type ModalSize = keyof typeof SIZE_PX;

/* ──────────────────────────────── Modal ──────────────────────────────── */

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = "md",
  placement = "center",
  confirmOnDirtyClose = false,
  closeOnBackdrop = true,
  morphName,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Кнопки внизу панели. Обычно пара «Отмена» / главное действие. */
  footer?: ReactNode;
  size?: ModalSize;
  /** «center» — по центру экрана; «pointer» — у курсора (формы-попапы HR). */
  placement?: "center" | "pointer";
  /** Спросить подтверждение, если в полях формы что-то введено (защита от
   *  случайного клика мимо, который стирал набранное). */
  confirmOnDirtyClose?: boolean;
  closeOnBackdrop?: boolean;
  /** Окно вырастает ИЗ строки, по которой кликнули (№18 каталога движения).
   *
   *  Передаём одно и то же имя участка сюда и на строку-источник, а открытие
   *  оборачиваем в runTabTransition/startViewTransition — браузер сам растянет
   *  строку в карточку и схлопнет обратно. Человек не теряет место в списке из
   *  трёхсот сотрудников: видно, откуда окно выросло и куда вернётся.
   *
   *  ⚠️ Имя обязано быть уникальным на экране в момент перехода: две строки с
   *  одним именем — и переход не проиграется вовсе. Поэтому подставляем в него
   *  идентификатор строки, а не общее слово. */
  morphName?: string;
  className?: string;
}) {
  const depth = useContext(DepthContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const idRef = useRef<string>("");
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const requestClose = useCallback(async () => {
    if (confirmOnDirtyClose) {
      const panel = panelRef.current;
      const filled = panel
        ? Array.from(
            panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea"),
          ).some((el) => el.type !== "checkbox" && el.type !== "radio" && el.value.trim() !== "")
        : false;
      if (filled) {
        const ok = await confirmDialog({
          title: "Закрыть форму?",
          text: "Введённые данные не сохранятся.",
          confirmText: "Закрыть",
          danger: true,
        });
        if (!ok) return;
      }
    }
    onClose();
  }, [confirmOnDirtyClose, onClose]);

  // ⚠️ Эффект регистрации обязан выполниться РОВНО ОДИН раз за жизнь окна.
  // Поэтому requestClose читается через ref, а не стоит в зависимостях: почти
  // все вызывающие передают onClose инлайн-стрелкой, из-за чего requestClose
  // пересоздаётся на каждый ререндер. С ним в зависимостях эффект перезапускался
  // на КАЖДЫЙ введённый символ, а его очистка возвращала фокус на кнопку —
  // в поле формы попадала только первая буква.
  const closeRef = useRef(requestClose);
  useEffect(() => {
    closeRef.current = requestClose;
  });

  const backdrop = useBackdropClose(() => void requestClose(), closeOnBackdrop);

  useEffect(() => {
    const id = `m${++seq}`;
    idRef.current = id;
    stack.push(id);
    lockScroll();
    const prevFocus = document.activeElement as HTMLElement | null;
    setMounted(true);

    const onKey = (e: KeyboardEvent) => {
      // Только верхнее окно реагирует на Esc, иначе одним нажатием
      // схлопывалась бы вся цепочка вложенных форм.
      if (e.key === "Escape" && stack[stack.length - 1] === id) {
        e.stopPropagation();
        void closeRef.current();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      const i = stack.indexOf(id);
      if (i >= 0) stack.splice(i, 1);
      unlockScroll();
      prevFocus?.focus?.();
    };
  }, []);

  // Фокус внутрь панели: первое поле формы, иначе сама панель.
  useEffect(() => {
    if (!mounted) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector<HTMLElement>(
      "input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])",
    );
    (first ?? panel).focus({ preventScroll: true });
  }, [mounted]);

  // placement="pointer": ставим панель рядом с точкой клика, зажимая в окно.
  //
  // Позиция считается ОДИН раз, при открытии — панель не должна ездить, пока
  // человек в ней работает (карточка сотрудника меняет высоту при каждом
  // переключении вкладки, и пересчёт превращался в прыжки).
  //
  // Раз двигать нельзя, место под рост надо занять СРАЗУ. Сколько занимать —
  // зависит от размера окна:
  //   • крупные (lg/xl) — это формы с вкладками и длинными списками. Им
  //     резервируется весь потолок (88dvh, тот же, что в классе панели):
  //     панель встаёт так, чтобы могла вырасти до него, не выходя за экран и
  //     не упираясь в прокрутку, пока на странице есть место. Раньше здесь
  //     резервировался остаток места ПОД курсором, и карточка, открытая из
  //     нижней половины схемы, показывала полосу прокрутки при свободном
  //     экране сверху.
  //   • мелкие (sm/md) — короткие формы, им хватает своей высоты: они и
  //     должны открываться вплотную к курсору.
  useLayoutEffect(() => {
    if (placement !== "pointer") return;
    const el = panelRef.current;
    if (!el) return;

    const place = () => {
      const margin = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = el.offsetWidth;
      const ceiling = Math.round(vh * 0.88); // = max-h-[88dvh] на панели

      let left = lastPointer.x + 8;
      if (left + w + margin > vw) left = lastPointer.x - w - 8; // не влезает справа — открываем слева
      left = Math.min(Math.max(margin, left), Math.max(margin, vw - w - margin));

      const roomy = size === "lg" || size === "xl";
      const reserve = Math.min(ceiling, roomy ? ceiling : el.offsetHeight);
      const top = Math.min(Math.max(margin, lastPointer.y + 8), Math.max(margin, vh - reserve - margin));
      setPos({ left, top });
    };

    place();
    // Только ресайз окна: на изменение высоты контента панель НЕ переставляем.
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [placement, mounted, size]);

  if (!mounted) return null;

  const z = Z.MODAL + depth * Z.MODAL_STEP;
  const atPointer = placement === "pointer";

  const panel = (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
      // ⚠️ `relative` обязателен в центрированном режиме: подложка ниже —
      // `absolute`, и позиционированный элемент рисуется ПОВЕРХ статичного.
      // Без этого затемнение ложилось на само окно (оно выглядело серым), а
      // клики по полям формы попадали в подложку и закрывали её.
      className={`flex max-h-[88dvh] w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] elev-modal outline-none ${
        atPointer ? "fixed" : "relative"
      } ${className ?? ""}`}
      style={{
        maxWidth: SIZE_PX[size],
        ...(morphName ? { viewTransitionName: morphName } : null),
        ...(atPointer
          ? {
              left: pos?.left ?? 12,
              top: pos?.top ?? 12,
              // До первого замера панель уже в DOM (иначе не измерить), но
              // показывать её в углу нельзя — прячем до вычисления позиции.
              visibility: pos ? "visible" : "hidden",
            }
          : null),
      }}
    >
      <div className="flex items-start justify-between gap-3 border-b border-[var(--border)] px-5 py-4">
        <div className="min-w-0">
          <h2 className="widget-title text-[16px] leading-tight">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[12px] text-[var(--textMuted)]">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={() => void requestClose()}
          aria-label="Закрыть"
          className="glass-flat -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] hover:text-[var(--text)]"
        >
          <IconClose size={18} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-5 py-3">
          {footer}
        </div>
      )}
    </div>
  );

  return createPortal(
    <DepthContext.Provider value={depth + 1}>
      <div
        className={`fixed inset-0 ${atPointer ? "" : "flex items-center justify-center p-4"}`}
        style={{ zIndex: z }}
        {...backdrop}
      >
        {/* Подложка: единая на весь портал. У формы-попапа она прозрачная —
            панель стоит у курсора и затемнять экран под ней незачем. */}
        <div
          className={atPointer ? "absolute inset-0" : "absolute inset-0 bg-black/40"}
          data-backdrop="1"
          aria-hidden
        />
        {panel}
      </div>
    </DepthContext.Provider>,
    document.body,
  );
}

/* ──────────────────────────────── Drawer ──────────────────────────────── */

/**
 * Выдвижная панель сбоку — карточка товара, детали строки.
 * До этого её каркас был скопирован в трёх product-drawer.tsx
 * (dev-tracker / product-launch / assortment) слово в слово.
 */
export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 520,
  side = "right",
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  side?: "right" | "left";
  className?: string;
}) {
  const depth = useContext(DepthContext);
  const [mounted, setMounted] = useState(false);
  const idRef = useRef<string>("");

  // onClose через ref — по той же причине, что и в Modal: инлайн-стрелка в
  // пропсах пересоздавала эффект на каждый ререндер, и возврат фокуса в его
  // очистке выбивал курсор из полей внутри панели.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  const backdrop = useBackdropClose(onClose, true);

  useEffect(() => {
    const id = `d${++seq}`;
    idRef.current = id;
    stack.push(id);
    lockScroll();
    const prevFocus = document.activeElement as HTMLElement | null;
    setMounted(true);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && stack[stack.length - 1] === id) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = stack.indexOf(id);
      if (i >= 0) stack.splice(i, 1);
      unlockScroll();
      prevFocus?.focus?.();
    };
  }, []);

  if (!mounted) return null;

  return createPortal(
    <DepthContext.Provider value={depth + 1}>
      <div
        className={`fixed inset-0 flex ${side === "right" ? "justify-end" : "justify-start"}`}
        style={{ zIndex: Z.MODAL + depth * Z.MODAL_STEP }}
        {...backdrop}
      >
        {/* Затемнение БЕЗ размытия. Панель выезжает поверх рабочего экрана, и
            содержимое под ней должно оставаться читаемым: из карточки товара
            часто сверяются со строкой в таблице позади. Размытие этому мешало и
            вдобавок заставляло браузер перерисовывать всю страницу под панелью. */}
        <div className="absolute inset-0 bg-black/40" data-backdrop="1" aria-hidden />
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          className={`relative flex h-dvh w-[min(100vw-32px,var(--drawer-w))] flex-col overflow-hidden border-[var(--border)] bg-[var(--surface)] elev-modal ${
            side === "right" ? "border-l" : "border-r"
          } ${className ?? ""}`}
          style={{ ["--drawer-w" as string]: `${width}px` }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-[var(--border)] px-5 py-4">
            <div className="min-w-0">
              <h2 className="widget-title text-[16px] leading-tight">{title}</h2>
              {subtitle && <p className="mt-0.5 text-[12px] text-[var(--textMuted)]">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть"
              className="glass-flat -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] hover:text-[var(--text)]"
            >
              <IconClose size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-5 py-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </DepthContext.Provider>,
    document.body,
  );
}
